import { useState, type CSSProperties, type ReactNode } from 'react';
import { Icon, type IconName } from '../editor/chrome/icons';
import { Avatar, Button } from '../ui/controls';
import { tip } from '../ui/tooltip';
import { colourFor, type Owner } from './rows';
import { tileUrl } from '../editor/content/tileUrl';

/** A project's colour when projects-back gave it none. */
export const NEUTRAL_DOT = 'var(--label-3)';

/** The shadow a page casts on the tile's ground. */
export const PAGE_SHADOW = 'shadow-[0_1px_2px_rgb(0_0_0/0.14),0_8px_22px_-8px_rgb(0_0_0/0.45)]';

/** A project's colour, as a small dot beside its name. */
export function Dot({ colour, size = 8 }: { colour: string | null; size?: number }) {
  return <span className="inline-block shrink-0 rounded-full" style={{ width: size, height: size, background: colour ?? NEUTRAL_DOT }} />;
}

/** A title with how many it holds. */
export function Title({ children, count, size = 26 }: { children: ReactNode; count?: string | number; size?: number }) {
  return (
    <h1 className="flex min-w-0 items-baseline gap-[10px] font-bold tracking-[-0.01em]" style={{ fontSize: size, lineHeight: `${size + 6}px` }}>
      <span className="truncate">{children}</span>
      {count !== undefined ? <span className="shrink-0 text-[15px] font-medium tabular-nums text-label-3">{count}</span> : null}
    </h1>
  );
}

export function SectionHead({ title, action, onAction }: { title: string; action?: string; onAction?: () => void }) {
  return (
    <div className="mb-[14px] flex items-baseline gap-[8px]">
      <h2 className="text-[17px] font-semibold">{title}</h2>
      <span className="flex-1" />
      {action ? (
        <button type="button" onClick={onAction} className="flex items-center gap-[2px] text-[13px] text-accent-text hover:underline">
          {action}
          <Icon name="chevron-right" size={14} />
        </button>
      ) : null}
    </div>
  );
}

/** A page on the ground: the file, before there is a picture of it. */
export function PageTile({ className = '', style }: { className?: string; style?: CSSProperties }) {
  return (
    <span className={`grid aspect-[3/4] place-items-center rounded-[4px] bg-white text-[#b8b8be] ${PAGE_SHADOW} ${className}`} style={style}>
      <Icon name="artboard" size={20} />
    </span>
  );
}

/**
 * The file's cover: its first artboard filling the tile edge to edge, the way Figma shows files
 * (Tarek's pick B, 2026-09-30: https://claude.ai/artifact/HVWqEq3JxyaCNaEypLym4C).
 *
 * Every tile is the same shape and all picture. A wide page fills it across; a tall one is cut
 * from the top, where a page's heading usually is. Pages used to stand at their own size in the
 * tile, so a wide file looked small beside a tall one and the names wandered under them.
 */
export function Thumb({
  url,
  className = '',
  eager = false,
  icon = 'artboard',
}: {
  url: string | null;
  className?: string;
  /** On screen at once (Recent): not left to lazy loading. */
  eager?: boolean;
  /** The mark on the empty tile: a file's artboard, a project's folder. */
  icon?: IconName;
}) {
  const [broken, setBroken] = useState(false);
  // Until the picture is here the tile is a quiet ground, and the picture fades in over it.
  const [loaded, setLoaded] = useState(false);
  const picture = url && !broken;
  return (
    <span
      className={`relative block overflow-hidden ring-1 ring-separator transition-shadow duration-150 group-hover:ring-2 group-hover:ring-label-3 ${picture ? 'bg-fill-2' : 'grid place-items-center bg-canvas text-label-4'} ${className}`}
    >
      {picture ? (
        <img
          src={tileUrl(url!, 640)}
          alt=""
          draggable={false}
          loading={eager ? 'eager' : 'lazy'}
          onLoad={() => setLoaded(true)}
          onError={() => setBroken(true)}
          className={`absolute inset-0 block size-full object-cover object-top transition-opacity duration-200 ${loaded ? 'opacity-100' : 'opacity-0'}`}
        />
      ) : (
        // No picture drawn yet: the file's mark on the canvas's own grey.
        <Icon name={icon} size={24} />
      )}
    </span>
  );
}

/** Someone's face: their picture, or their initial on their own colour. */
export function Face({ owner, size = 18 }: { owner: Owner; size?: number }) {
  const [broken, setBroken] = useState(false);
  const name = owner.name ?? 'Unknown';
  if (owner.avatarUrl && !broken)
    return (
      <img
        src={owner.avatarUrl}
        alt={name}
        {...tip(name)}
        onError={() => setBroken(true)}
        className="shrink-0 rounded-full object-cover"
        style={{ width: size, height: size, boxShadow: '0 0 0 1.5px var(--glass-thick)' }}
      />
    );
  return <Avatar name={name} colour={colourFor(owner.id)} size={size} />;
}

/** Show More under a list that has another page. */
export function ShowMore({ list }: { list: { rows: readonly unknown[] | null; more: boolean; loadingMore: boolean; loadMore: () => void } }) {
  if (!list.rows || !list.more) return null;
  return (
    <div className="mt-[24px] flex justify-center">
      <Button onClick={list.loadMore} disabled={list.loadingMore}>
        Show More
      </Button>
    </div>
  );
}

export function ErrorLine({ children, className = '' }: { children: ReactNode; className?: string }) {
  return (
    <p role="alert" className={`mb-[16px] text-[13px] text-label-2 ${className}`}>
      {children}
    </p>
  );
}

export function Nothing({ children = 'Nothing here.' }: { children?: ReactNode }) {
  return <p className="py-[48px] text-center text-[13px] text-label-2">{children}</p>;
}

/** A grey stand-in for a card while its list loads. */
export function PlaceholderCard({ radius = 12 }: { radius?: number }) {
  return (
    <div aria-hidden className="min-w-0">
      <span className="block aspect-[4/3] bg-fill-2" style={{ borderRadius: radius }} />
      <span className="mt-[10px] block h-[10px] w-[62%] rounded-full bg-fill-2" />
      <span className="mt-[6px] block h-[8px] w-[38%] rounded-full bg-fill-3" />
    </div>
  );
}
